import { CalendarIcon, Star, StarOff, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomerSearchMultiSelect } from '@/components/shared/customer-search-multi-select';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multiselect';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import type { CreatePromiseFormCustomer } from '@/hooks/useCreatePromiseData';
import { useCurrenciesList } from '@/hooks/useCurrenciesData';
import { filtersMatch } from '@/hooks/useFilterPreferences';
import { isRangeInvalid, parseRangeInput, RANGE_MIN_GREATER_THAN_MAX_MESSAGE } from '@/lib/range-filter-validation';
import { cn, formatDate, parseLocalDate, toLocalDateString } from '@/lib/utils';
import type { SavedFilterPreset, TransactionFilters, TransactionStatus, TransactionType } from '@/types/transaction';

const transactionTypes: { value: TransactionType; label: string }[] = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'credit_note', label: 'Credit Note' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'debit_adjustment', label: 'Debit Adjustment' },
  { value: 'credit_adjustment', label: 'Credit Adjustment' },
];

const statuses: { value: TransactionStatus; label: string }[] = [
  { value: 'outstanding', label: 'Outstanding' },
  { value: 'completed', label: 'Completed' },
  { value: 'overdue', label: 'Overdue' },
];

interface FilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: TransactionFilters;
  onApplyFilters: (filters: TransactionFilters) => void;
  savedPresets: SavedFilterPreset[];
  activePresetId: string | null;
  onApplyPreset: (preset: SavedFilterPreset) => void;
  onDeletePreset: (presetId: string) => void;
  onSavePreset: (draftFilters: TransactionFilters) => void;
  onClearAll: () => void;
}

export function FilterSheet({
  open,
  onOpenChange,
  filters,
  onApplyFilters,
  savedPresets,
  activePresetId,
  onApplyPreset,
  onDeletePreset,
  onSavePreset,
  onClearAll,
}: FilterSheetProps) {
  const [draftFilters, setDraftFilters] = useState<TransactionFilters>({ ...filters });
  const [dateFromOpen, setDateFromOpen] = useState(false);
  const [dateToOpen, setDateToOpen] = useState(false);
  const [dueDateFromOpen, setDueDateFromOpen] = useState(false);
  const [dueDateToOpen, setDueDateToOpen] = useState(false);
  const [anticipatedDateFromOpen, setAnticipatedDateFromOpen] = useState(false);
  const [anticipatedDateToOpen, setAnticipatedDateToOpen] = useState(false);
  const draftPresetRef = useRef<SavedFilterPreset | null>(null);

  // Sync draft with applied filters when sheet opens
  useEffect(() => {
    if (open) {
      setDraftFilters({ ...filters });
      draftPresetRef.current = null;
    }
  }, [open, filters]);

  // Track selected customer objects for the multi-select (keeps labels in sync)
  const [selectedCustomers, setSelectedCustomers] = useState<CreatePromiseFormCustomer[]>([]);

  // Sync selected customers when the sheet opens with pre-existing filter IDs
  // (customers already selected from a previous session are kept as-is)
  useEffect(() => {
    if (open) {
      // Keep only the customers whose IDs are still in the draft filters
      setSelectedCustomers((prev) => prev.filter((c) => draftFilters.customerIds.includes(parseInt(c.id, 10))));
    }
  }, [open, draftFilters.customerIds]);

  const handleCustomerSelectionChange = useCallback((customers: CreatePromiseFormCustomer[]) => {
    setSelectedCustomers(customers);
    setDraftFilters((prev) => ({
      ...prev,
      customerIds: customers.map((c) => parseInt(c.id, 10)),
    }));
  }, []);

  // Mode-aware data for dropdowns
  const { data: currencies } = useCurrenciesList();

  const currencyOptions = (currencies ?? []).map((c) => ({
    value: c.code,
    label: `${c.code} - ${c.name}`,
  }));

  const amountRangeInvalid = isRangeInvalid(draftFilters.amountMin, draftFilters.amountMax);
  const outstandingRangeInvalid = isRangeInvalid(
    draftFilters.outstandingBalanceMin,
    draftFilters.outstandingBalanceMax,
  );
  const hasInvalidRange = amountRangeInvalid || outstandingRangeInvalid;

  const handleApply = () => {
    if (hasInvalidRange) return;

    if (draftPresetRef.current) {
      onApplyPreset(draftPresetRef.current);
    } else {
      onApplyFilters(draftFilters);
    }
    draftPresetRef.current = null;
    onOpenChange(false);
  };

  const handlePresetClick = (preset: SavedFilterPreset) => {
    setDraftFilters({ ...preset.filters });
    draftPresetRef.current = preset;
  };

  const handleClearDraft = () => {
    setSelectedCustomers([]);
    draftPresetRef.current = null;
    setDraftFilters({
      customerIds: [],
      transactionTypes: [],
      statuses: [],
      currencies: [],
      searchQuery: '',
      hasCase: undefined,
      hasPromise: undefined,
      onDisputeFilter: undefined,
      externalDisputeFilter: undefined,
    });
  };

  // Determine which preset is visually "active" in the sheet:
  // If a preset was clicked (draft), use that; otherwise fall back to the applied activePresetId
  // but only if the draft filters still match that preset.
  const displayActivePresetId = useMemo(() => {
    if (draftPresetRef.current) {
      return filtersMatch(draftFilters, draftPresetRef.current.filters) ? draftPresetRef.current.id : null;
    }
    if (!activePresetId) return null;
    const appliedPreset = savedPresets.find((x) => x.id === activePresetId);
    if (!appliedPreset) return null;
    return filtersMatch(draftFilters, appliedPreset.filters) ? activePresetId : null;
  }, [draftFilters, activePresetId, savedPresets]);

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      // Discard draft changes when closing without applying
      onOpenChange(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col overflow-hidden">
        <SheetHeader className="px-6">
          <SheetTitle>Filter Transactions</SheetTitle>
          <SheetDescription>Apply filters to narrow down your transaction list</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto pr-4 pl-4 mt-6">
          <div className="space-y-6 pb-4">
            {/* Saved Presets */}
            {savedPresets.length > 0 && (
              <div className="space-y-3">
                <Label className="text-base font-semibold">Saved Presets</Label>
                <div className="space-y-2">
                  {savedPresets.map((preset) => (
                    <div
                      key={preset.id}
                      className="flex items-center gap-2 p-2 rounded-md border bg-card hover:bg-accent/50 transition-colors"
                    >
                      <button
                        onClick={() => handlePresetClick(preset)}
                        className="flex items-center gap-2 flex-1 text-left"
                      >
                        {displayActivePresetId === preset.id ? (
                          <Star className="h-4 w-4 fill-primary text-primary" />
                        ) : (
                          <StarOff className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="text-sm font-medium">{preset.name}</span>
                      </button>
                      {!preset.isDefault && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeletePreset(preset.id)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Separator />
              </div>
            )}

            {/* Customer Filter */}
            <div className="space-y-2">
              <Label>Customer</Label>
              <CustomerSearchMultiSelect
                selectedCustomers={selectedCustomers}
                onSelectionChange={handleCustomerSelectionChange}
                placeholder="Select customers..."
              />
            </div>

            {/* Transaction Type Filter */}
            <div className="space-y-2">
              <Label>Transaction Type</Label>
              <MultiSelect
                options={transactionTypes}
                placeholder="Select transaction types..."
                emptyMessage="No transaction types found."
                value={draftFilters.transactionTypes}
                onValueChange={(value) =>
                  setDraftFilters({ ...draftFilters, transactionTypes: value as TransactionType[] })
                }
              />
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label>Status</Label>
              <MultiSelect
                options={statuses}
                placeholder="Select statuses..."
                emptyMessage="No statuses found."
                value={draftFilters.statuses}
                onValueChange={(value) => setDraftFilters({ ...draftFilters, statuses: value as TransactionStatus[] })}
              />
            </div>

            {/* Currency Filter */}
            <div className="space-y-2">
              <Label>Currency</Label>
              <MultiSelect
                options={currencyOptions}
                placeholder="Select currencies..."
                emptyMessage="No currencies found."
                value={draftFilters.currencies || []}
                onValueChange={(value) => setDraftFilters({ ...draftFilters, currencies: value })}
              />
            </div>

            <Separator />

            {/* Transaction Date Range Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Transaction Date Range</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date-from" className="text-sm">
                    From
                  </Label>
                  <Popover open={dateFromOpen} onOpenChange={setDateFromOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="date-from"
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !draftFilters.dateFrom && 'text-muted-foreground',
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {draftFilters.dateFrom ? formatDate(draftFilters.dateFrom) : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={draftFilters.dateFrom ? parseLocalDate(draftFilters.dateFrom) : undefined}
                        onSelect={(date) => {
                          setDraftFilters({
                            ...draftFilters,
                            dateFrom: date ? toLocalDateString(date) : undefined,
                          });
                          setDateFromOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="date-to" className="text-sm">
                    To
                  </Label>
                  <Popover open={dateToOpen} onOpenChange={setDateToOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="date-to"
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !draftFilters.dateTo && 'text-muted-foreground',
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {draftFilters.dateTo ? formatDate(draftFilters.dateTo) : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={draftFilters.dateTo ? parseLocalDate(draftFilters.dateTo) : undefined}
                        onSelect={(date) => {
                          setDraftFilters({
                            ...draftFilters,
                            dateTo: date ? toLocalDateString(date) : undefined,
                          });
                          setDateToOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <Separator />

            {/* Due Date Range */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Due Date Range</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="due-date-from" className="text-sm">
                    From
                  </Label>
                  <Popover open={dueDateFromOpen} onOpenChange={setDueDateFromOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="due-date-from"
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !draftFilters.dueDateFrom && 'text-muted-foreground',
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {draftFilters.dueDateFrom ? formatDate(draftFilters.dueDateFrom) : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={draftFilters.dueDateFrom ? parseLocalDate(draftFilters.dueDateFrom) : undefined}
                        onSelect={(date) => {
                          setDraftFilters({
                            ...draftFilters,
                            dueDateFrom: date ? toLocalDateString(date) : undefined,
                          });
                          setDueDateFromOpen(false);
                        }}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="due-date-to" className="text-sm">
                    To
                  </Label>
                  <Popover open={dueDateToOpen} onOpenChange={setDueDateToOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="due-date-to"
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !draftFilters.dueDateTo && 'text-muted-foreground',
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {draftFilters.dueDateTo ? formatDate(draftFilters.dueDateTo) : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={draftFilters.dueDateTo ? parseLocalDate(draftFilters.dueDateTo) : undefined}
                        onSelect={(date) => {
                          setDraftFilters({
                            ...draftFilters,
                            dueDateTo: date ? toLocalDateString(date) : undefined,
                          });
                          setDueDateToOpen(false);
                        }}
                        autoFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <Separator />

            {/* Anticipated Date Range */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Anticipated Date Range</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="anticipated-date-from" className="text-sm">
                    From
                  </Label>
                  <Popover open={anticipatedDateFromOpen} onOpenChange={setAnticipatedDateFromOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="anticipated-date-from"
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !draftFilters.anticipatedDateFrom && 'text-muted-foreground',
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {draftFilters.anticipatedDateFrom
                          ? formatDate(draftFilters.anticipatedDateFrom)
                          : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          draftFilters.anticipatedDateFrom
                            ? parseLocalDate(draftFilters.anticipatedDateFrom)
                            : undefined
                        }
                        onSelect={(date) => {
                          setDraftFilters({
                            ...draftFilters,
                            anticipatedDateFrom: date ? toLocalDateString(date) : undefined,
                          });
                          setAnticipatedDateFromOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="anticipated-date-to" className="text-sm">
                    To
                  </Label>
                  <Popover open={anticipatedDateToOpen} onOpenChange={setAnticipatedDateToOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        id="anticipated-date-to"
                        variant="outline"
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !draftFilters.anticipatedDateTo && 'text-muted-foreground',
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {draftFilters.anticipatedDateTo ? formatDate(draftFilters.anticipatedDateTo) : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          draftFilters.anticipatedDateTo ? parseLocalDate(draftFilters.anticipatedDateTo) : undefined
                        }
                        onSelect={(date) => {
                          setDraftFilters({
                            ...draftFilters,
                            anticipatedDateTo: date ? toLocalDateString(date) : undefined,
                          });
                          setAnticipatedDateToOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <Separator />

            {/* Amount Range Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Amount Range</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount-min" className="text-sm">
                    Min Amount
                  </Label>
                  <Input
                    id="amount-min"
                    type="number"
                    step="any"
                    placeholder="0"
                    aria-invalid={amountRangeInvalid}
                    value={draftFilters.amountMin ?? ''}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        amountMin: parseRangeInput(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount-max" className="text-sm">
                    Max Amount
                  </Label>
                  <Input
                    id="amount-max"
                    type="number"
                    step="any"
                    placeholder="No limit"
                    aria-invalid={amountRangeInvalid}
                    value={draftFilters.amountMax ?? ''}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        amountMax: parseRangeInput(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              {amountRangeInvalid && (
                <p role="alert" className="text-sm text-destructive">
                  {RANGE_MIN_GREATER_THAN_MAX_MESSAGE}
                </p>
              )}
            </div>

            <Separator />

            {/* Flags Filters */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Flags</Label>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-external-dispute"
                    checked={draftFilters.externalDisputeFilter ?? false}
                    onCheckedChange={(checked) =>
                      setDraftFilters({
                        ...draftFilters,
                        externalDisputeFilter: checked === true ? true : undefined,
                      })
                    }
                  />
                  <label htmlFor="filter-external-dispute" className="text-sm font-medium leading-none cursor-pointer">
                    External Dispute
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-has-dispute"
                    checked={draftFilters.onDisputeFilter ?? false}
                    onCheckedChange={(checked) =>
                      setDraftFilters({
                        ...draftFilters,
                        onDisputeFilter: checked === true ? true : undefined,
                      })
                    }
                  />
                  <label htmlFor="filter-has-dispute" className="text-sm font-medium leading-none cursor-pointer">
                    Has Dispute
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-has-promise"
                    checked={draftFilters.hasPromise ?? false}
                    onCheckedChange={(checked) =>
                      setDraftFilters({
                        ...draftFilters,
                        hasPromise: checked === true ? true : undefined,
                      })
                    }
                  />
                  <label htmlFor="filter-has-promise" className="text-sm font-medium leading-none cursor-pointer">
                    Has Promise to Pay
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="filter-has-case"
                    checked={draftFilters.hasCase ?? false}
                    onCheckedChange={(checked) =>
                      setDraftFilters({
                        ...draftFilters,
                        hasCase: checked === true ? true : undefined,
                      })
                    }
                  />
                  <label htmlFor="filter-has-case" className="text-sm font-medium leading-none cursor-pointer">
                    Has Collection Case
                  </label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Outstanding Balance Range Filter */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Outstanding Balance</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="outstanding-min" className="text-sm">
                    Min Outstanding
                  </Label>
                  <Input
                    id="outstanding-min"
                    type="number"
                    step="any"
                    placeholder="0"
                    aria-invalid={outstandingRangeInvalid}
                    value={draftFilters.outstandingBalanceMin ?? ''}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        outstandingBalanceMin: parseRangeInput(e.target.value),
                      })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="outstanding-max" className="text-sm">
                    Max Outstanding
                  </Label>
                  <Input
                    id="outstanding-max"
                    type="number"
                    step="any"
                    placeholder="No limit"
                    aria-invalid={outstandingRangeInvalid}
                    value={draftFilters.outstandingBalanceMax ?? ''}
                    onChange={(e) =>
                      setDraftFilters({
                        ...draftFilters,
                        outstandingBalanceMax: parseRangeInput(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              {outstandingRangeInvalid && (
                <p role="alert" className="text-sm text-destructive">
                  {RANGE_MIN_GREATER_THAN_MAX_MESSAGE}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <SheetFooter className="p-6 bg-background border-t">
          <div className="flex gap-2 w-full">
            <Button variant="outline" onClick={handleClearDraft} className="flex-1">
              Clear All
            </Button>
            <Button
              variant="outline"
              onClick={() => onSavePreset(draftFilters)}
              disabled={hasInvalidRange}
              className="flex-1"
            >
              Save Preset
            </Button>
            <Button onClick={handleApply} disabled={hasInvalidRange} className="flex-1">
              Apply Filters
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
